import { createAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import DiseaseActivityApi from 'api/DiseaseActivityApi';
import { showNotification } from 'app-redux/notification.saga';
import * as dto from 'client/dto';

import { updateOutbreakAndAirportLayer } from 'map/map.saga';

import { fetchDiseaseActivitiesTemporal } from 'diseaseActivitiesTemporal/diseaseActivitiesTemporal.saga';
import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import { userLocationIdParam2AoiPayload } from 'diseaseActivityPanels/disacPanelUtils';
import { closePanel } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import {
  DiseaseActivityParams,
  PanelType,
} from 'diseaseActivityPanels/diseaseActivityPanels.slice';

import {
  loadDiseaseActivitiesError,
  loadDiseaseActivitiesStart,
  loadDiseaseActivitiesSuccess,
  loadDiseaseActivityDetailsError,
  loadDiseaseActivityDetailsStart,
  loadDiseaseActivityDetailsSuccess,
  loadRiskParametersCalculationBreakdownError,
  loadRiskParametersCalculationBreakdownStart,
  loadRiskParametersCalculationBreakdownSuccess,
} from './diseaseActivitiesInCountryLocations.slice';
import diseaseActivityListSelectors from './diseaseActivityList.selectors';

export type SelectDiseaseActivityUpdatesSagaPayload = {
  activityDate?: string;
  excludeCaseCounts: boolean;
};

export const fetchDiseaseEvents = createAction('diseaseActivitiesInCountryLocations/DiseaseEvents');
export const fetchDiseaseActivityList = createAction(
  'diseaseActivitiesInCountryLocations/fetchDiseaseActivityList'
);
export const fetchDiseaseActivityDetails = createAction(
  'diseaseActivitiesInCountryLocations/fetchDiseaseActivityDetails'
);
export const fetchCalculationBreakdown = createAction(
  'diseaseActivitiesInCountryLocations/fetchCalculationBreakdown'
);
export const clearDiseaseActivityUpdates = createAction(
  'diseaseActivitiesInCountryLocations/clearDiseaseActivityUpdates'
);

export function* fetchDiseaseActivityListSaga() {
  try {
    const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId } = disacParams;

    yield put(loadDiseaseActivitiesStart());
    const response: dto.GetEventListModel = yield call(
      DiseaseActivityApi.DiseaseActivityController_GetDiseaseActivities,
      {
        diseaseId: diseaseId,
        speciesId: speciesId,
        userLocationId: userLocationIdParam2AoiPayload(userLocationId),
        activityStartDate: startDate,
      }
    );
    yield put(fetchDiseaseActivitiesTemporal());
    yield put(loadDiseaseActivitiesSuccess(response));

    const isSelectedEventInvalid: boolean = yield select(
      diseaseActivityListSelectors.isSelectedEventInvalid
    );
    if (isSelectedEventInvalid) {
      yield put(
        showNotification({
          type: 'SHOW_NEUTRAL_NOTIFICATION',
          payload: `Selected activity is not present in the current list`,
        })
      );
      yield put(closePanel(PanelType.DiseaseActivityDetailsPanel));
    }
    yield put(updateOutbreakAndAirportLayer());
  } catch (e) {
    yield put(loadDiseaseActivitiesError());
  }
}

export function* fetchDiseaseActivityDetailsSaga() {
  try {
    const startDate: string = yield select(diseaseActivitiesTimeFrameSelectors.getStartDate);
    const disacParams: DiseaseActivityParams = yield select(
      diseaseActivityPanelsSelectors.disacParams
    );
    const { userLocationId, diseaseId, speciesId, countryId } = disacParams;

    yield put(loadDiseaseActivityDetailsStart());
    const response: dto.GetEventModel = yield call(
      DiseaseActivityApi.DiseaseActivityController_GetDiseaseActivity,
      {
        diseaseId: diseaseId,
        speciesId: speciesId,
        countryId: countryId,
        userLocationId: userLocationIdParam2AoiPayload(userLocationId),
        activityStartDate: startDate,
      }
    );
    yield put(loadDiseaseActivityDetailsSuccess(response));
    yield put(updateOutbreakAndAirportLayer());
  } catch (e) {
    yield put(loadDiseaseActivityDetailsError());
  }
}

export function* fetchCalculationBreakdownSaga() {
  const disacParams: DiseaseActivityParams = yield select(
    diseaseActivityPanelsSelectors.disacParams
  );
  const { userLocationId, diseaseId, speciesId, countryId } = disacParams;

  try {
    yield put(loadRiskParametersCalculationBreakdownStart());
    const response: dto.CalculationBreakdownModel = yield call(
      DiseaseActivityApi.DiseaseActivityController_GetCalculationBreakdown,
      {
        userLocationId: userLocationId,
        diseaseId: diseaseId,
        speciesId: speciesId,
        countryId: countryId,
      }
    );
    yield put(loadRiskParametersCalculationBreakdownSuccess(response));
  } catch (e) {
    yield put(loadRiskParametersCalculationBreakdownError());
  }
}

function* diseaseActivitiesInCountryLocationsSaga() {
  yield takeLatest(fetchDiseaseActivityList, fetchDiseaseActivityListSaga);
  yield takeLatest(fetchDiseaseActivityDetails, fetchDiseaseActivityDetailsSaga);
  yield takeLatest(fetchCalculationBreakdown, fetchCalculationBreakdownSaga);
}

export default diseaseActivitiesInCountryLocationsSaga;
